/**
 * Account-feature API layer + i18n namespace registration — STORY-001-010.
 *
 * The `account` i18n namespace is registered here via
 * `i18n.addResourceBundle` rather than in `apps/web/src/lib/i18n.ts`
 * (which is NOT in this story's allowed-touch list). The bundles are
 * loaded once at module import; `react-i18next`'s `useTranslation
 * ("account")` then resolves keys with the documented RU-fallback chain
 * (REQ-PRD-§13.2-A — a missing KY/EN key falls back to RU). All three
 * locales (ru/ky/en) ship per ADR-028.
 *
 * Types mirror the backend Pydantic shapes in
 * `apps/api/garden/auth/schemas.py` (MeResponse, SessionsResponse, etc.).
 * Once `packages/shared-types` OpenAPI codegen lands (later story) these
 * hand-written interfaces are replaced by the generated DTOs.
 *
 * The calls go through the shared `apiFetch` wrapper so every mutating
 * request carries an `Idempotency-Key` (REQ-ADD-§6.3-A) and the
 * 401→refresh→retry dance + RFC 9457 error parsing come for free.
 */
import i18n from "@/lib/i18n";
import { apiClient, apiFetch, getApiClientConfig } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";

import accountEn from "@/locales/en/account.json";
import accountKy from "@/locales/ky/account.json";
import accountRu from "@/locales/ru/account.json";

const ACCOUNT_NS = "account";

let accountI18nRegistered = false;

/**
 * Register the `account` i18n namespace (ru/ky/en) on the shared
 * i18next singleton. Idempotent + init-safe: ``addResourceBundle`` is
 * only callable once the instance is initialized (``initI18n()`` runs in
 * `main.tsx` at app boot and in each test's `beforeEach`), so we guard
 * on ``i18n.isInitialized`` and the method's presence and remember that
 * we've run. Called at the top of each account screen's render so the
 * keys are present regardless of import order.
 *
 * The 4th ``addResourceBundle`` arg ``deep=true`` merges nested objects;
 * the 5th ``overwrite=false`` keeps an existing bundle (HMR-safe).
 */
export function registerAccountI18n(): void {
  if (accountI18nRegistered) return;
  if (!i18n.isInitialized || typeof i18n.addResourceBundle !== "function") {
    return;
  }
  i18n.addResourceBundle("ru", ACCOUNT_NS, accountRu, true, false);
  i18n.addResourceBundle("ky", ACCOUNT_NS, accountKy, true, false);
  i18n.addResourceBundle("en", ACCOUNT_NS, accountEn, true, false);
  accountI18nRegistered = true;
}

// Best-effort eager registration for the production path (initI18n() runs
// before any feature module imports in main.tsx). In tests where init
// happens in beforeEach, the screens call registerAccountI18n() on render.
registerAccountI18n();

export type AccountLanguage = "ru" | "ky" | "en";

/** Mirror of ``garden.auth.schemas.MeResponse``. */
export interface MeProfile {
  id: string;
  email: string;
  phone_e164: string | null;
  phone_verified_at: string | null;
  preferred_language: AccountLanguage;
  region: string | null;
  display_name: string;
  created_at: string;
  roles: string[];
  /**
   * Resolved permission slugs for this user (Phase 0.2 extension).
   * Empty array for non-admin users. Used by `AdminGuard` to check
   * `marketplace.seller.approve`.
   */
  permissions: string[];
  /**
   * Derived seller-ownership flag (Track 0 persona-auth, ADR-035): true
   * when the user owns a live `sellers.user_id` link. NOT an RBAC role —
   * computed at read time, no stored column. Lets the seller door route a
   * seller without an optimistic 403 bounce.
   */
  is_seller: boolean;
  /** The owned seller's id when `is_seller` is true, else null. */
  seller_id: string | null;
}

/** Mirror of ``garden.auth.schemas.UserProfileUpdate`` (all optional). */
export interface ProfileUpdate {
  display_name?: string;
  region?: string | null;
  preferred_language?: AccountLanguage;
}

/** Mirror of ``garden.auth.schemas.SessionInfo``. */
export interface SessionInfo {
  family_id: string;
  user_agent: string | null;
  ip_masked: string | null;
  issued_at: string;
  last_used_at: string;
  is_current: boolean;
}

/** Mirror of ``garden.auth.schemas.SessionsResponse``. */
export interface SessionsResponse {
  sessions: SessionInfo[];
}

/** Mirror of ``garden.auth.schemas.AccountDeleteResponse``. */
export interface DeleteResponse {
  job_id: string;
  estimated_completion: string;
}

/** Server endpoint paths owned by `apps/api/garden/auth/router_me.py` etc. */
export const ME_PATHS = {
  me: "/me",
  sessions: "/me/sessions",
  revokeOthers: "/me/sessions/revoke-others",
  export: "/me/export",
  delete: "/me/delete",
  revokeOne: (familyId: string): string =>
    "/me/sessions/" + encodeURIComponent(familyId) + "/revoke",
} as const;

/** Empty JSON body for the mutating endpoints that take no payload. */
type EmptyBody = Record<string, never>;

export function getMe(): Promise<MeProfile> {
  return apiClient.get<MeProfile>(ME_PATHS.me);
}

export function patchMe(body: ProfileUpdate): Promise<MeProfile> {
  // No `apiClient.patch` convenience wrapper exists; call `apiFetch`
  // directly with a concrete `TBody` (a non-undefined body type keeps
  // `exactOptionalPropertyTypes` happy).
  return apiFetch<MeProfile, ProfileUpdate>({
    method: "PATCH",
    path: ME_PATHS.me,
    body,
  });
}

export function getSessions(): Promise<SessionsResponse> {
  return apiClient.get<SessionsResponse>(ME_PATHS.sessions);
}

export function revokeSession(familyId: string): Promise<void> {
  // The path-param revoke takes no body; send `{}` so `TBody` is a
  // concrete object type rather than `undefined`.
  return apiClient.post<void, EmptyBody>(ME_PATHS.revokeOne(familyId), {});
}

export function revokeOtherSessions(): Promise<void> {
  return apiClient.post<void, EmptyBody>(ME_PATHS.revokeOthers, {});
}

export function deleteAccount(confirmPhrase: string): Promise<DeleteResponse> {
  return apiClient.post<DeleteResponse, { confirm_phrase: string }>(
    ME_PATHS.delete,
    { confirm_phrase: confirmPhrase },
  );
}

// ─────────────────────────── data export ───────────────────────────

/** Outcome of a "Download my data" attempt — drives the toast/message. */
export type ExportOutcome = "ok" | "rate_limited" | "error";

/**
 * Fetch `GET /me/export` (a ZIP) with the in-memory Bearer token and trigger a
 * browser download. Returns a discriminated outcome so the caller renders the
 * right localized message (429 → `rate_limited`).
 *
 * `fetch` + object-URL rather than a plain `<a download>`: the access token
 * lives in JS memory (REQ-PRD-§11.2-A — never localStorage, never a readable
 * cookie), so an anchor cannot carry the Authorization header.
 *
 * This lived in BOTH `features/settings/api.ts` and inline in `PrivacyScreen`
 * as two copies that had already drifted apart (different filenames on the
 * download, different error taxonomies). One copy, next to `ME_PATHS`.
 */
export async function downloadMyData(): Promise<ExportOutcome> {
  try {
    const accessToken = useAuthStore.getState().accessToken;
    const headers: Record<string, string> = { Accept: "application/zip" };
    if (accessToken !== null) {
      headers["Authorization"] = "Bearer " + accessToken;
    }
    const res = await fetch(getApiClientConfig().baseUrl + ME_PATHS.export, {
      method: "GET",
      headers,
      credentials: "include",
    });
    if (res.status === 429) return "rate_limited";
    if (!res.ok) return "error";
    const blob = await res.blob();
    triggerBlobDownload(blob, "garden-export.zip");
    return "ok";
  } catch {
    return "error";
  }
}

/** Trigger a client-side download of `blob` as `filename` (object-URL). */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick so the click has consumed the URL.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
